import { Power4, gsap } from 'gsap';
import { Helper } from '../../utils/Helper';
import { SymbolData } from './config/SymbolData';
import { ISymbolAnimationConf } from './ReelBaseSymbol';
import { ISymbolConfig } from './config/IReelSymbolStruct';
import { ReelSpriteSymbolArea, ReelSymbolStates } from './ReelSpriteSymbolArea';

export class ReelSpriteSymbol extends ReelSpriteSymbolArea {
    constructor(ID: number, symName: string, state: string = ReelSymbolStates.NORMAL, type?: number) {
        super(ID, symName, state, type);
    }

    protected fetchMainTimeline(vars?: gsap.TimelineVars, triggerEnd: boolean = false): gsap.core.Timeline {
        return Helper.setTimeline(this._timeLine, new gsap.core.Timeline(vars), triggerEnd);
    }

    protected fetchSubTimeline(vars?: gsap.TimelineVars, triggerEnd: boolean = false): gsap.core.Timeline {
        return Helper.setTimeline(this._subTimeline, new gsap.core.Timeline(vars), triggerEnd);
    }

    public skip(): void {
        this._timeLine?.totalProgress(1);
        this._subTimeline?.totalProgress(1);
    }

    public changeSymbol(symName: string): void {
        this.changeState({
            sym: symName,
            state: this.state.value,
            forceState: true,
        });
    }

    public changeState(stateConfig: ISymbolAnimationConf): gsap.core.Timeline {
        if (!stateConfig.sizeType) stateConfig.sizeType = this.sizeType.value;
        if (!stateConfig.forceState && this.state.value === stateConfig.state && this.sizeType.value === stateConfig.sizeType)
            return this._timeLine ? this._timeLine : new gsap.core.Timeline();
        else return this.setSymbolState(stateConfig);
    }

    private gfxTl: gsap.core.Timeline;

    public markNonWin(enable: boolean, alpha: number = 0.35, durr: number = 0.25): void {
        this.gfxTl = Helper.setTimeline(
            this.gfxTl,
            new gsap.core.Timeline({
                onComplete: () => {
                    this._darkSquareGfx.alpha = enable ? alpha : 0;
                },
            }),
            true
        );
        this.gfxTl.to(this._darkSquareGfx, { duration: durr, alpha: enable ? alpha : 0 });
    }

    protected setSymbolState(config: ISymbolAnimationConf): gsap.core.Timeline {
        this._timeLine = Helper.setTimeline(
            this._timeLine,
            new gsap.core.Timeline({
                onComplete: () => {
                    if (this.subSp && this.mainSp && this.parent) {
                        this.subSp.scale = this.mainSp.scale;
                        this.subSp.texture = this.mainSp.texture;
                    }
                },
            }),
            true
        );

        const toConf: ISymbolConfig = config.sym ? SymbolData.getConfig(config.sym!) : this.symbolConfig;
        const toSizeType: any =
            config.sizeType && this.sizeType.value != config.sizeType ? config.sizeType! : this.sizeType.value;
        const toState: any = config.state && this.state.value != config.state ? config.state! : this.state.value;

        const TEXTURE_FROM: PIXI.Texture[] | undefined = SymbolData.getTextures(
            this.symbolConfig.NAME,
            this.state.value,
            this.sizeType.value
        );
        const TEXTURES_TO: PIXI.Texture[] | undefined = SymbolData.getTextures(toConf.NAME, toState, toSizeType);

        this.state.set(toState);
        this.expandAnimation(toSizeType, config.duration ? config.duration : 0, config.ease);
        this.sizeType.set(toSizeType);
        this.symbolConfig = toConf;

        if (config.duration)
            this.textureAnimation(TEXTURE_FROM, TEXTURES_TO, config.duration, config.ease ? config.ease : Power4.easeInOut);
        else if (TEXTURES_TO) {
            this.mainSp!.texture = this.subSp!.texture = TEXTURES_TO[0];
        }
        return this._timeLine;
    }

    protected expandAnimation(size: number, _duration: number, _ease: any): void {
        if (!this.sizeType.equal(size)) {
            const currSize: number = SymbolData.fetchSizeTimesSize(this.symName, this.state.value, this.sizeType.value);
            const expectedSize: number = SymbolData.fetchSizeTimesSize(this.symName, this.state.value, size.toString());

            const toScale: PIXI.Point = new PIXI.Point(1, expectedSize / currSize);
            const fromScale: PIXI.Point = new PIXI.Point(1, currSize / expectedSize);

            // Texture
            this._timeLine!.fromTo(
                this.mainSp.scale,
                { x: fromScale.x, y: fromScale.y },
                { x: 1, y: 1, duration: _duration, ease: _ease },
                0
            );
            this._timeLine!.fromTo(
                this.subSp.scale,
                { x: 1, y: 1 },
                { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease },
                0
            );

            // GFX
            this._timeLine!.fromTo(
                [this._darkSquareGfx.scale],
                { x: 1, y: 1 },
                { x: toScale.x, y: toScale.y, duration: _duration, ease: _ease },
                0
            );
            this.sizeType.set(size);
        }
    }

    protected textureAnimation(
        _TextureFrom: PIXI.Texture[] | undefined,
        _TextureTo: PIXI.Texture[] | undefined,
        _duration: number,
        _ease?: any
    ): void {
        if (_TextureTo) {
            this.mainSp!.texture = _TextureTo[0];
            this._timeLine!.fromTo(this.mainSp, { alpha: 0 }, { duration: _duration, alpha: 1, ease: _ease }, 0);

            if (_TextureFrom) {
                this.subSp!.texture = _TextureFrom[0];
                this._timeLine!.fromTo(this.subSp, { alpha: 1 }, { duration: _duration, alpha: 0, ease: _ease }, 0);
            }
        }
    }
}
